import { useEffect } from "react";
import { useFlowType } from "@lending/hooks/useFlowType";
import { isInProgressApplicationState } from "@lending/utils/applicationState";
import {
  type AutoLoanApplicationContext,
  useApplicationNavigationContext,
} from "./applicationNavigationContext";
import { ResolvedApplicationLayout } from "./ResolvedApplicationLayout";
import { getCreatingResumeStepId } from "./resumeRouteResolver";
import { useResolvedAutoLoanApplication } from "./useResolvedAutoLoanApplication";
import { useStore } from "../../contexts/StoreContext";
import type { VehicleLoanApplication } from "@lending/types";

interface AutoLoanApplicationLayoutProps {
  fallbackPath: string;
}

interface InProgressAutoLoanApplicationLayoutProps extends AutoLoanApplicationLayoutProps {
  application: VehicleLoanApplication;
  isLoading: boolean;
  isError: boolean;
  applicationState?: VehicleLoanApplication["state"];
  accountApplicationUuid?: string | null;
  shouldAlwaysEnforceRoute: boolean;
  flowType: "web" | "olb";
  autoLoanContext: AutoLoanApplicationContext;
  canonicalStepId: string | null;
}

function InProgressAutoLoanApplicationLayout({
  application,
  autoLoanContext: autoLoanContext,
  fallbackPath,
  isLoading,
  isError,
  applicationState,
  accountApplicationUuid,
  shouldAlwaysEnforceRoute,
  flowType,
  canonicalStepId: policyCanonicalStepId,
}: InProgressAutoLoanApplicationLayoutProps) {
  const navigationContext = useApplicationNavigationContext(
    application,
    "auto-loan",
    autoLoanContext
  );
  const canonicalStepId =
    application.state === "creating"
      ? getCreatingResumeStepId(application, "auto-loan", flowType, navigationContext)
      : policyCanonicalStepId;

  // Persist the derived resume facts into the store so downstream `goBack`
  // / `goNext` calls (which read `storeData.*` directly) route through the
  // right flow branches after the initial redirect completes.
  const { updateData } = useStore();
  const {
    skippedPlaidPrefill,
    hasCoApplicant,
    hasSeparateCoApplicantAddress,
    incomeType,
    coApplicantIncomeType,
    hasVehicleSelected,
    isRefinance,
  } = navigationContext;
  useEffect(() => {
    updateData({
      skippedPlaidPrefill,
      hasCoApplicant,
      hasSeparateCoApplicantAddress,
      incomeType,
      coApplicantIncomeType,
      hasVehicleSelected,
      isRefinanceSelected: isRefinance,
    });
  }, [
    coApplicantIncomeType,
    hasCoApplicant,
    hasSeparateCoApplicantAddress,
    hasVehicleSelected,
    incomeType,
    isRefinance,
    skippedPlaidPrefill,
    updateData,
  ]);

  return (
    <ResolvedApplicationLayout
      fallbackPath={fallbackPath}
      productType="auto-loan"
      isLoading={isLoading}
      isError={isError}
      canonicalStepId={canonicalStepId}
      applicationState={applicationState}
      navigationContext={navigationContext}
      accountApplicationUuid={accountApplicationUuid}
      shouldAlwaysEnforceRoute={shouldAlwaysEnforceRoute}
    />
  );
}

function AutoLoanApplicationLayout({ fallbackPath }: AutoLoanApplicationLayoutProps) {
  const flowType = useFlowType();
  const {
    isLoading,
    isError,
    canonicalStepId,
    currentStepId,
    applicationState,
    application,
    autoLoanContext: autoLoanContext,
    accountApplicationUuid,
    shouldAlwaysEnforceRoute,
  } = useResolvedAutoLoanApplication();
  if (application && isInProgressApplicationState(application.state) && currentStepId === null) {
    return (
      <InProgressAutoLoanApplicationLayout
        fallbackPath={fallbackPath}
        application={application}
        isLoading={isLoading}
        isError={isError}
        applicationState={applicationState}
        accountApplicationUuid={accountApplicationUuid}
        shouldAlwaysEnforceRoute={shouldAlwaysEnforceRoute}
        flowType={flowType}
        autoLoanContext={autoLoanContext}
        canonicalStepId={canonicalStepId}
      />
    );
  }

  return (
    <ResolvedApplicationLayout
      fallbackPath={fallbackPath}
      productType="auto-loan"
      isLoading={isLoading}
      isError={isError}
      canonicalStepId={canonicalStepId}
      applicationState={applicationState}
      navigationContext={autoLoanContext}
      accountApplicationUuid={accountApplicationUuid}
      shouldAlwaysEnforceRoute={shouldAlwaysEnforceRoute}
    />
  );
}

export { AutoLoanApplicationLayout };
